import { Trans, useTranslation } from 'next-i18next';
import { ChartBarFillIcon, MagicwandFillIcon, SparklingFillIcon } from '@/icons';
import { useCountryCode } from '@/shared/hooks';
import { PLAN_FEATURES } from '@/shared/plan/consts';
import { type GroupPricingContent } from '../PricingSection1/groupPricingContent';
import { TableHeader } from './TableHeader';
import { TableSection } from './TableSection';
import { type FeatureSection } from './types';
export const PlanComparisonTable = ({
  groupPricingContent
}: {
  groupPricingContent: GroupPricingContent;
}) => {
  const {
    t
  } = useTranslation(['common', 'pricing']);
  const {
    countryCode,
    isKR
  } = useCountryCode();
  const planFeatures = PLAN_FEATURES[isKR ? 'KR' : 'GLOBAL'];
  const TABLE_DATA: FeatureSection[] = [{
    id: 'PREMIUM_FEATURES',
    title: t('pricing:planComparison.premiumFeatures'),
    icon: <SparklingFillIcon size="xl" className="[&>*]:fill-alternative" />,
    features: [{
      id: 'CONCURRENT_USERS',
      label: t('pricing:planComparison.concurrentUsers'),
      tooltipText: t('pricing:planComparison.concurrentUsersTooltip'),
      data: {
        FREE: {
          isSupported: true,
          text: t('pricing:planComparison.maxNum', {
            num: 200
          })
        },
        BASIC: {
          isSupported: true,
          text: t('pricing:planComparison.maxNum', {
            num: 200
          })
        },
        PRO: {
          isSupported: true,
          text: t('pricing:planComparison.maxNum', {
            num: 200
          })
        }
      }
    }, {
      id: 'PREMIUM_MAPS',
      label: t('pricing:planComparison.premiumMaps'),
      tooltipText: t('pricing:planComparison.premiumMapsTooltip'),
      data: {
        FREE: {
          isSupported: true,
          text: t('pricing:planComparison.someLimited')
        },
        BASIC: {
          isSupported: true,
          text: `+${t('pricing:planComparison.basicExclusiveMap')}`
        },
        PRO: {
          isSupported: true,
          text: `+${t('pricing:planComparison.allMaps')}`
        }
      }
    }, {
      id: 'PRIVATE_QUIZ',
      label: t('pricing:planComparison.privateQuiz'),
      tooltipText: t('pricing:planComparison.privateQuizTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('PRIVATE_QUIZ')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('PRIVATE_QUIZ')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('PRIVATE_QUIZ')
        }
      }
    }, {
      id: 'REMOVE_ADS',
      label: t('pricing:planComparison.removeAds'),
      tooltipText: t('pricing:planComparison.removeAdsTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('REMOVE_ADS')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('REMOVE_ADS')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('REMOVE_ADS')
        }
      },
      visibleCountries: ['ID']
    }, {
      id: 'WORKSHEET',
      label: t('pricing:planComparison.worksheet'),
      tooltipText: t('pricing:planComparison.worksheetTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('WORKSHEET')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('WORKSHEET')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('WORKSHEET')
        }
      }
    }, {
      id: 'CONTINUE_NEXT_QUIZSET',
      label: t('pricing:planComparison.continueNextQuiz'),
      tooltipText: t('pricing:planComparison.continueNextQuizTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('CONTINUE_NEXT_QUIZSET')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('CONTINUE_NEXT_QUIZSET')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('CONTINUE_NEXT_QUIZSET')
        }
      }
    }]
  }, {
    id: 'LEARNING_REPORT_FEATURES',
    title: t('pricing:planComparison.learningReport'),
    icon: <ChartBarFillIcon size="xl" className="fill-alternative" />,
    features: [{
      id: 'STUDENT_STATS',
      label: t('pricing:planComparison.studentStats'),
      tooltipText: t('pricing:planComparison.studentStatsTooltip'),
      data: {
        FREE: {
          isSupported: true
        },
        BASIC: {
          isSupported: true
        },
        PRO: {
          isSupported: true
        }
      }
    }, {
      id: 'QUESTION_STATS',
      label: t('pricing:planComparison.questionStats'),
      tooltipText: t('pricing:planComparison.questionStatsTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('LMS_PARTIAL')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('LMS_PARTIAL')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('LMS_PARTIAL')
        }
      }
    }, {
      id: 'OVERALL_STATS',
      label: t('pricing:planComparison.overallStats'),
      tooltipText: t('pricing:planComparison.overallStatsTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('LMS_PARTIAL')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('LMS_PARTIAL')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('LMS_PARTIAL')
        }
      }
    }, {
      id: 'AI_TUTOR_CHAT_HISTORY',
      label: t('pricing:planComparison.aiTutorChatHistory'),
      tooltipText: t('pricing:planComparison.aiTutorChatHistoryTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('LMS_PARTIAL')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('LMS_PARTIAL')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('LMS_PARTIAL')
        }
      },
      visibleCountries: ['KR']
    }]
  }, {
    id: 'QUIZ_AI_FEATURES',
    title: t('pricing:planComparison.quizAi'),
    icon: <MagicwandFillIcon size="xl" className="[&>*]:fill-alternative" />,
    features: [{
      id: 'AI_FROM_QUERY',
      label: <Trans t={t} i18nKey={'pricing:planComparison.aiFromQuery'} components={{
        Br: <br className="hidden laptop-sm:block" />
      }} />,
      tooltipText: t('pricing:planComparison.aiFromQueryTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_FROM_QUERY')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_FROM_QUERY')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_FROM_QUERY')
        }
      }
    }, {
      id: 'AI_FROM_FILE',
      label: <Trans t={t} i18nKey={'pricing:planComparison.aiFromFile'} components={{
        Br: <br className="hidden laptop-sm:block" />
      }} />,
      tooltipText: `${t('pricing:planComparison.aiFromFileTooltip')} (pdf, csv, txt, docx, pptx, hwp)`,
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_FROM_FILE')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_FROM_FILE')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_FROM_FILE')
        }
      }
    }, {
      id: 'AI_FROM_YOUTUBE',
      label: <Trans t={t} i18nKey={'pricing:planComparison.aiFromYoutube'} components={{
        Br: <br className="hidden laptop-sm:block" />
      }} />,
      tooltipText: t('pricing:planComparison.aiFromYoutubeTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_FROM_YOUTUBE')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_FROM_YOUTUBE')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_FROM_YOUTUBE')
        }
      }
    }, {
      id: 'AI_SIMILAR_QUIZ_GENERATION',
      label: t('pricing:planComparison.aiSimilarQuiz'),
      tooltipText: t('pricing:planComparison.aiSimilarQuizTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_SIMILAR_QUIZ_GENERATION')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_SIMILAR_QUIZ_GENERATION')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_SIMILAR_QUIZ_GENERATION')
        }
      }
    }, {
      id: 'AI_CHOICE_GENERATION',
      label: t('pricing:planComparison.aiChoiceGeneration'),
      tooltipText: t('pricing:planComparison.aiChoiceGenerationTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_CHOICE_GENERATION')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_CHOICE_GENERATION')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_CHOICE_GENERATION')
        }
      }
    }, {
      id: 'AI_QUIZSET_TRANSLATION',
      label: t('pricing:planComparison.aiTranslation'),
      tooltipText: t('pricing:planComparison.aiTranslationTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_QUIZSET_TRANSLATION')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_QUIZSET_TRANSLATION')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_QUIZSET_TRANSLATION')
        }
      }
    }, {
      id: 'AI_TUTOR',
      label: t('pricing:planComparison.aiTutor'),
      tooltipText: t('pricing:planComparison.aiTutorTooltip'),
      data: {
        FREE: {
          isSupported: planFeatures.FREE.has('AI_TUTOR')
        },
        BASIC: {
          isSupported: planFeatures.BASIC.has('AI_TUTOR')
        },
        PRO: {
          isSupported: planFeatures.PRO.has('AI_TUTOR')
        }
      },
      visibleCountries: ['KR']
    }]
  }];
  const sections = TABLE_DATA.map(section => ({
    ...section,
    features: section.features.filter(feature => feature.visibleCountries?.includes(countryCode) ?? true)
  }));
  return <div className="px-md tablet-sm:px-lg tablet-lg:px-2xl" data-sentry-component="PlanComparisonTable" data-sentry-source-file="PlanComparisonTable.tsx">
      <div className="min-w-[528px]">
        <TableHeader groupPricingContent={groupPricingContent} data-sentry-element="TableHeader" data-sentry-source-file="PlanComparisonTable.tsx" />
        {sections.map(section => <TableSection key={section.id} section={section} />)}
      </div>
    </div>;
};